import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase";
import { Ticket, Copy, Check, Sparkle, Truck } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Storefront Coupons section — reads live from Supabase (`coupons` table)
 * with realtime updates so admin changes appear on the site without a
 * reload. Cards support one-click copy of the code.
 */
export default function Coupons() {
  const [items, setItems] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from("coupons")
        .select("*")
        .order("is_featured", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(20);
      if (cancelled) return;
      if (error) {
        setItems([]);
        return;
      }
      // Client-side filter: only show active + visible + within date window
      const rows = (data || []).filter((c) => {
        if (c.is_active === false) return false;
        if (c.show_on_site === false) return false;
        if (c.starts_at && c.starts_at > nowIso) return false;
        if (c.expires_at && c.expires_at < nowIso) return false;
        if (c.max_uses && c.used_count && c.used_count >= c.max_uses) return false;
        return true;
      });
      setItems(rows.slice(0, 6));
    };
    load();
    const channel = supabase
      .channel("storefront-coupons")
      .on("postgres_changes", { event: "*", schema: "public", table: "coupons" }, load)
      .subscribe();
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, []);

  const copy = async (c) => {
    try {
      await navigator.clipboard.writeText(c.code);
      setCopiedId(c.id);
      toast.success(`Copied ${c.code}`);
      setTimeout(() => setCopiedId((cur) => (cur === c.id ? null : cur)), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  if (items.length === 0) return null;

  const valueLabel = (c) => {
    // A pure free-delivery coupon (no discount) shows "FREE DELIVERY" instead of "0% OFF"
    if (c.free_delivery && (!c.discount_value || Number(c.discount_value) === 0)) return "FREE DELIVERY";
    return c.discount_type === "flat"
      ? `${(c.currency || "INR") === "INR" ? "₹" : "$"}${Number(c.discount_value || 0).toLocaleString("en-IN")} OFF`
      : `${Number(c.discount_value || 0)}% OFF`;
  };

  const fmtExpiry = (s) => {
    if (!s) return null;
    const d = new Date(s);
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
  };

  return (
    <section
      id="coupons"
      data-testid="coupons-section"
      className="scroll-mt-24 md:scroll-mt-28 lg:scroll-mt-32 py-14 sm:py-20 bg-cool-white"
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-6 reveal">
          <div className="max-w-2xl">
            <div className="overline text-cyan-brand inline-flex items-center gap-1.5">
              <Sparkle size={12} weight="fill" /> Offers &amp; savings
            </div>
            <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-[54px] font-bold text-navy-deep tracking-tight leading-[1.05]">
              Coupons you can use today.
            </h2>
            <p className="mt-4 text-slate-600 text-base max-w-lg">
              Tap any code to copy it, then paste at checkout. Limited-time offers
              disappear as soon as they end.
            </p>
          </div>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((c, i) => {
            const featured = !!c.is_featured;
            const expiry = fmtExpiry(c.expires_at);
            return (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                onClick={() => copy(c)}
                data-testid={`coupon-card-${c.code}`}
                className={`group cursor-pointer relative overflow-hidden rounded-3xl border transition-all duration-300 hover:-translate-y-1 ${
                  featured
                    ? "border-transparent bg-signature-gradient text-white shadow-[0_25px_60px_-25px_rgba(2,167,196,0.55)]"
                    : "border-[#8A97A8]/25 bg-white text-navy-deep hover:shadow-[0_25px_60px_-30px_rgba(2,47,107,0.25)] hover:border-cyan-brand/40"
                }`}
              >
                {/* Perforated left edge (classic coupon look) */}
                <div className={`absolute top-0 bottom-0 left-14 w-px ${featured ? "bg-white/30" : "bg-slate-200"}`} style={{ backgroundImage: `radial-gradient(circle, currentColor 1px, transparent 1.5px)`, backgroundSize: "1px 8px", opacity: 0.5 }} />

                <div className="flex items-stretch">
                  {/* Left rail with ticket icon */}
                  <div className={`w-14 shrink-0 flex items-center justify-center ${featured ? "bg-white/10" : "bg-slate-50"}`}>
                    <Ticket size={22} weight="duotone" className={featured ? "text-white" : "text-cyan-brand"} />
                  </div>

                  <div className="flex-1 p-6">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {c.badge && (
                            <span className={`overline text-[9.5px] px-2 py-0.5 rounded-full ${featured ? "bg-white/20 text-white" : "bg-cyan-brand/10 text-cyan-brand"}`}>
                              {c.badge}
                            </span>
                          )}
                          {c.free_delivery && (
                            <span className={`overline text-[9.5px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${featured ? "bg-white/20 text-white" : "bg-emerald-500/10 text-emerald-600"}`}>
                              <Truck size={10} weight="bold" /> Free delivery
                            </span>
                          )}
                        </div>
                        <div className="mt-2 font-mono-data font-bold tracking-[0.15em] text-[15px]">
                          {c.code}
                        </div>
                        {c.title && (
                          <div className={`mt-1 text-[13px] ${featured ? "text-white/85" : "text-slate-500"}`}>
                            {c.title}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className={`font-display font-bold text-2xl leading-none tabular-nums ${featured ? "" : "text-navy-deep"}`}>
                          {valueLabel(c)}
                        </div>
                      </div>
                    </div>

                    {c.description && (
                      <p className={`mt-4 text-[13.5px] leading-relaxed ${featured ? "text-white/90" : "text-slate-600"}`}>
                        {c.description}
                      </p>
                    )}

                    <div className="mt-5 flex items-center justify-between">
                      <div className={`text-[11px] ${featured ? "text-white/70" : "text-slate-400"}`}>
                        {c.min_order > 0 && (
                          <span>Min order {(c.currency || "INR") === "INR" ? "₹" : "$"}{Number(c.min_order).toLocaleString("en-IN")}</span>
                        )}
                        {c.min_order > 0 && expiry && <span className="mx-2">·</span>}
                        {expiry && <span>Ends {expiry}</span>}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); copy(c); }}
                        className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[12px] font-semibold transition-all ${
                          featured
                            ? "bg-white text-navy-deep hover:bg-white/90"
                            : "bg-navy-deep text-white hover:bg-navy"
                        }`}
                        aria-label={`Copy coupon ${c.code}`}
                      >
                        {copiedId === c.id ? (
                          <><Check size={13} weight="bold" /> Copied</>
                        ) : (
                          <><Copy size={13} weight="bold" /> Copy code</>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
